import { useState, useEffect } from 'react';
import { showAlert } from '@/components/AppDialog';
import { doc, getDoc } from 'firebase/firestore';
import { saveAdminConfig } from '@/utils/adminConfig';
import { db } from '../firebase';
import { logAdminAction } from '@/utils/audit';
import AdminLayout from '../components/AdminLayout';
import { FileText, Save, CheckCircle2, RotateCcw, Info, Sparkles, FileEdit } from 'lucide-react';
import {
  DEFAULT_PERSONA, DEFAULT_BAND_LABELS, GOAL_COLUMNS, R_CODES,
  mergePersonas, type PersonaCell,
} from '@/data/assessmentContent';

const BAND_TONE: Record<string, string> = {
  R1: '#D7443E', R2: '#E8743B', R3: '#E8A53B', R4: '#E8C23B', R5: '#3171af', R6: '#1976d2', R7: '#15824E',
};

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v));

export default function AdminContentStudio() {
  const [personas, setPersonas] = useState<Record<string, PersonaCell[]>>(() => clone(DEFAULT_PERSONA));
  const [bandLabels, setBandLabels] = useState<Record<string, string>>(() => ({ ...DEFAULT_BAND_LABELS }));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'assessmentContent', 'current'));
        if (snap.exists()) {
          const d = snap.data() as any;
          if (d.personas) setPersonas(mergePersonas(d.personas));
          if (d.bandLabels && typeof d.bandLabels === 'object') setBandLabels({ ...DEFAULT_BAND_LABELS, ...d.bandLabels });
        }
      } catch (e) {
        console.error('load assessmentContent failed', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const editPersona = (r: string, col: number, field: 0 | 1, val: string) =>
    setPersonas(p => ({
      ...p,
      [r]: p[r].map((cell, i) => (i === col ? ([field === 0 ? val : cell[0], field === 1 ? val : cell[1]] as PersonaCell) : cell)),
    }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveAdminConfig('assessmentContent/current', { personas, bandLabels });
      setSavedAt(new Date().toLocaleTimeString());
      logAdminAction('config.update', 'assessmentContent/current', { keys: ['personas', 'bandLabels'] });
    } catch (e: any) {
      console.error('save failed', e);
      showAlert(e?.code === 'permission-denied'
        ? 'Save failed. Permission denied. This editor needs a Firebase-authenticated admin session.'
        : 'Save failed. Check console.');
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => { setPersonas(clone(DEFAULT_PERSONA)); setBandLabels({ ...DEFAULT_BAND_LABELS }); setSavedAt(null); };

  const inp = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand-soft)]';

  return (
    <AdminLayout
      section="Content"
      title="Content Studio"
      icon={FileEdit}
      subtitle="Readiness Journey copy: the headings, blurbs and step text clients read in their dashboard."
      spaced
      actions={<>
        {savedAt && <span className="ax-saved"><CheckCircle2 /> Saved {savedAt}</span>}
        <button onClick={resetDefaults} className="btn"><RotateCcw /> Reset to defaults</button>
        <button onClick={handleSave} disabled={saving} className="btn btn-pri">
          <Save /> {saving ? 'Saving…' : 'Save changes'}
        </button>
      </>}
    >
      <div className="flex items-start gap-3 rounded-xl border border-[var(--brand-soft)] bg-[var(--brand-soft)]/60 px-4 py-3">
        <Info className="w-4 h-4 text-[var(--brand-d)] mt-0.5 shrink-0" />
        <p className="text-sm text-[var(--brand-d)]/80">
          Edit the words founders see on their <b>Readiness Journey</b> results: the 28 persona titles &amp; descriptions
          (Goal × Investor-Readiness) and the 7 risk-band labels. This is display text only; the underlying scoring is
          unchanged. Empty fields fall back to the built-in defaults.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-3 border-[var(--brand)] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <>
          {/* Band labels */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
            <h2 className="text-xs font-extrabold uppercase tracking-wide text-gray-500 mb-4 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[var(--brand-d)]" /> Risk-band labels</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
              {R_CODES.map(r => (
                <div key={r}>
                  <label className="text-[11px] font-bold uppercase tracking-wide mb-1 flex items-center gap-1.5" style={{ color: BAND_TONE[r] }}>
                    <span className="w-2 h-2 rounded-full" style={{ background: BAND_TONE[r] }} /> {r}
                  </label>
                  <input className={inp} value={bandLabels[r]} onChange={e => setBandLabels(b => ({ ...b, [r]: e.target.value }))} />
                </div>
              ))}
            </div>
          </div>

          {/* Persona matrix */}
          {R_CODES.map(r => (
            <div key={r} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="h-1.5" style={{ background: BAND_TONE[r] }} />
              <div className="p-5">
                <h2 className="text-sm font-extrabold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full text-white" style={{ background: BAND_TONE[r] }}>{r}</span>
                  {bandLabels[r]} <span className="text-gray-300 font-normal">· personas by goal</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  {GOAL_COLUMNS.map((g, col) => (
                    <div key={g.key} className="rounded-xl border border-gray-100 bg-gray-50/60 p-3">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-2">{g.label} <span className="text-gray-300">({g.key})</span></div>
                      <input
                        className={`${inp} mb-2 font-semibold`}
                        placeholder="Persona name"
                        value={personas[r]?.[col]?.[0] ?? ''}
                        onChange={e => editPersona(r, col, 0, e.target.value)}
                      />
                      <textarea
                        className={`${inp} resize-y`}
                        rows={3}
                        placeholder="Description"
                        value={personas[r]?.[col]?.[1] ?? ''}
                        onChange={e => editPersona(r, col, 1, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </AdminLayout>
  );
}
